import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CallGetProducts } from '@library/request/callProduct';
import { numberWithCommas } from '@library/Utils';
import CommonEditIconButton from '@components/molecules/button/EditIconButton';
import Spinner from '@components/molecules/Spinner';
import { MENUTYPE, PRODUCT_KEYS, Products, ProductsChild, ProductsOption } from '@type/productType';
import { useMenuPermission } from '@src/recoil/permission';

// 메뉴 상세 정보
const MenuDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const storeId = searchParams.get('storeID');
  const productID = searchParams.get('productID');

  const { isNoAuth2, isNoAuth3 } = useMenuPermission(storeId);

  // 가격 옵션, 가격 옵션 아닌 옵션들
  const [options, setOptions] = useState<{ priceOption: ProductsOption | any; nonePriceOptions: ProductsOption[] }>({
    priceOption: {},
    nonePriceOptions: [],
  });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // 메뉴 조회 API
  const { data: productDetail } = useQuery<Products>([PRODUCT_KEYS.PRODUCT, storeId, productID], {
    enabled: !!storeId && !!productID,
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetProducts(storeId, productID);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!productDetail?.options) return;

    setOptions({
      priceOption: productDetail.options?.find((option) => Number(option.isPriceOption) === 1),
      nonePriceOptions: productDetail.options?.filter((option) => Number(option.isPriceOption) !== 1),
    });
  }, [productDetail?.options]);

  return (
    <article id="mArticle" className="art-product menu-detail-info">
      <div className="box-product-info">
        <div className="head-product">
          <strong className="tit-product">메뉴 정보</strong>
          <CommonEditIconButton
            disable={isNoAuth3}
            onClick={() => navigate(`/product/form?storeID=${storeId}&productID=${productID}`)}
            title="수정"
          />
        </div>
        <div className="body-product">
          <strong id="categoryName" className="tit-info">
            {productDetail?.categoryName || ''}
          </strong>
          <em id="productName" className="emph-info">
            {productDetail?.name || ''}
            {productDetail?.alcohol === MENUTYPE.alcohol && <span className="alcohol-badge">19</span>}
          </em>
          <p id="productDescription" className="desc-info">
            {productDetail?.productDescription || ''}
          </p>
          <div className="box-info">
            <table id="PriceOption" className="tbl-info">
              <tbody>
                {options?.priceOption?.children?.map((option: ProductsChild) => {
                  const { optionID = '', name = '', price = 0, menuCouponCount, alcohol } = option;
                  return (
                    <tr key={optionID}>
                      <th>
                        {Number(menuCouponCount) > 0 && <span className="badge-coupon">메뉴 쿠폰</span>}
                        {name || '기본'} {alcohol === MENUTYPE.alcohol && <span className="alcohol-badge">19</span>}
                      </th>
                      <td>{`${numberWithCommas(price.toString() || '')}원`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="box-product-info">
        <div className="head-product">
          <strong className="tit-product">옵션 정보</strong>
          <CommonEditIconButton
            disable={isNoAuth2}
            onClick={() => navigate(`/product/connect?storeID=${storeId}&productID=${productID}`)}
            title="수정"
          />
        </div>
        <div className="body-product">
          <ul id="optionGroup" className="list-option-group">
            {options?.nonePriceOptions.map((option) => {
              const { title = '', children = [], optionGroupID, isRequired = 0 } = option;
              const visibleChildren = children.length > 3 ? (!isOpen ? children.slice(0, 3) : children) : children;
              return (
                <li key={optionGroupID}>
                  <strong className="tit-info">{title}</strong>
                  <span className="txt-count">{`(총 ${children.length}개)`} </span>
                  {isRequired === 1 && <span className="red">*필수선택</span>}
                  <div className="box-info fold">
                    <table className="tbl-info">
                      <tbody>
                        {visibleChildren?.map((list: ProductsChild) => {
                          const { optionID, name = '', price = 0 } = list;
                          return (
                            <tr key={optionID}>
                              <th>
                                {name} {list.alcohol === '1' && <span className="alcohol-badge">19</span>}
                              </th>
                              <td>{`${numberWithCommas(price.toString() || '')}원`}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {children.length > 3 && (
                      <button type="button" className="btn-fold" onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? '접기' : '더보기'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <Spinner show={loading} />
    </article>
  );
};

export default MenuDetail;
